/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* Copyright 2026 Bytefinger Technology (HK) Co., Limited. SPDX-License-Identifier: Apache-2.0 */

const MAX_RETRIES = 7;
const RETRY_DELAY_MS = 1500;

interface TraceResult {
  data?: { spans?: Array<{ attr_user_input?: Record<string, unknown> }> };
}

// Loop exports asynchronously. Retry detail reads only; never duplicate a write
// or retry an authorization/network failure. Callers retain their stale-request guards.
export async function requestTraceWithRetry<T>(
  url: string,
  request: () => Promise<T>,
  signal?: { readonly aborted: boolean },
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    const result = await request();
    const pending = (result as TraceResult)?.data?.spans?.some(
      span => span.attr_user_input?.['adp.trace_state'] === 'syncing',
    );
    if (
      !url.includes('/trace/by_') ||
      !pending ||
      attempt >= MAX_RETRIES ||
      signal?.aborted
    ) {
      return result;
    }
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
    if (signal?.aborted) {
      return result;
    }
  }
}
